// S5b — saved analysis sessions. Backed by localStorage until a
// real /v1/analysis/sessions endpoint lands. The keying scheme is
// intentionally simple so the swap to a remote store is just
// replacing this module's listing + persist functions.
//
// Why localStorage (not MSW): sessions persist across tab reloads
// and survive HMR cycles, which is what an operator expects when
// they save a workbench snapshot during diagnosis. MSW resets on
// every reload.

export type SavedSession = {
  id: string;
  name: string;
  assetId?: string;
  sensors?: string[];
  baseline?: string;
  /** Cursor time, ms since epoch — matches /analyze search-param shape. */
  t?: number;
  pins?: number[];
  createdAt: string;
};

const KEY = 'pdm.sessions.v1';

function readAll(): SavedSession[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items: SavedSession[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Storage may be full or disabled (private mode); swallow so
    // the UI doesn't blow up — the operator will see the row
    // wasn't persisted and try again.
  }
}

export function listSessions(): SavedSession[] {
  return readAll().sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}

export function saveSession(s: Omit<SavedSession, 'id' | 'createdAt'>): SavedSession {
  const next: SavedSession = {
    ...s,
    id: `sess_${Math.random().toString(36).slice(2, 10)}`,
    createdAt: new Date().toISOString(),
  };
  writeAll([next, ...readAll()]);
  return next;
}

export function deleteSession(id: string) {
  writeAll(readAll().filter((s) => s.id !== id));
}
